import { AxiosResponse } from 'axios';
import apiClient from '../axios';
import { getPaginatedEndpointParams } from '@api/helpers';
import { convertObjectToFormData } from '@helpers/apiHelper';
import {
  IGetResourceResponse,
  IDeleteResourceResponse,
  INoDataResponse,
  IPaginatedResourceEndpointResponse,
  IUpsertSlugResourceResponse,
} from '../interfaces';
import {
  ICreateCompetitionParams,
  IDuplicateCompetitionParams,
  IResumeCompetitionParams,
  IStartCompetitionParams,
  IUpdateCompetitionParams,
  ManageCompetition,
  ManageCompetitionsSort,
  PortalCompetition,
  PortalCompletedCompetition,
  PortalCompetitionEnrollment,
  PortalScoreSubmission,
  PortalCompetitionRanking,
  PortalCompetitionSession,
  ISubmitScoreSubmissionParams,
  ISubmitScoreSubmissionPayload,
  CompetitionEnrollment,
  CompetitionEnrollmentWithdrawal,
  CompetitionEnrollmentWithdrawalsSort,
  CompetitionEnrollmentsSort,
  CompetitionSessionsSort,
  CompetitionSubmissionsSort,
  ManageScoreSubmission,
  ManageCompetitionSession,
  IUpsertCompetitionSessionParams,
  ManageCompetitionSeries,
  ManageSeriesParticipant,
  IUpsertSeriesParticipantScoresParams,
} from './interfaces';

export const getPortalCompetitions = async (
  page: number,
  perPage: number,
  options?: { enrolledOnly?: boolean }
): Promise<IPaginatedResourceEndpointResponse<PortalCompetition>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<PortalCompetition>
  > = await apiClient.get('/competitions', {
    params: getPaginatedEndpointParams({
      page,
      perPage,
      filters: options?.enrolledOnly
        ? [
            {
              field: 'enrolled',
              operator: '=',
              value: true,
            },
          ]
        : undefined,
    }),
  });

  return response.data;
};

export const getPortalCompletedCompetitions = async (
  page: number,
  perPage: number
): Promise<IPaginatedResourceEndpointResponse<PortalCompletedCompetition>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<PortalCompletedCompetition>
  > = await apiClient.get('/competitions/results', {
    params: getPaginatedEndpointParams({
      page,
      perPage,
    }),
  });

  return response.data;
};

export const getPortalCompetition = async (
  slug: string
): Promise<PortalCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<PortalCompetition>> =
    await apiClient.get(`/competitions/${slug}`);

  return response.data.data;
};

export const getPortalCompletedCompetition = async (
  slug: string
): Promise<PortalCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<PortalCompetition>> =
    await apiClient.get(`/competitions/results/${slug}`);

  return response.data.data;
};

export const getManageCompetitions = async (
  page: number,
  perPage: number,
  sort?: ManageCompetitionsSort,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<ManageCompetition>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<ManageCompetition>
  > = await apiClient.get('/manage/competitions', {
    params: getPaginatedEndpointParams({
      page,
      perPage,
      sort,
      search,
    }),
  });

  return response.data;
};

export const getManageCompetition = async (
  slug: string
): Promise<ManageCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetition>> =
    await apiClient.get(`/manage/competitions/${slug}`);

  return response.data.data;
};

export const createCompetition = async (
  competitionData: ICreateCompetitionParams
): Promise<string> => {
  const formData = convertObjectToFormData(competitionData);

  const response: AxiosResponse<IUpsertSlugResourceResponse> =
    await apiClient.post('/manage/competitions', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

  return response.data.data.slug;
};

export const updateCompetition = async (
  slug: string,
  competitionData: IUpdateCompetitionParams
): Promise<string> => {
  const formData = convertObjectToFormData(competitionData, 'put');

  const response: AxiosResponse<IUpsertSlugResourceResponse> =
    await apiClient.post(`/manage/competitions/${slug}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

  return response.data.data.slug;
};

export const startCompetition = async (
  slug: string,
  params: IStartCompetitionParams
): Promise<ManageCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetition>> =
    await apiClient.post(`/manage/competitions/${slug}/start`, params);

  return response.data.data;
};

export const stopCompetition = async (
  slug: string,
  stopReason: string
): Promise<ManageCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetition>> =
    await apiClient.post(`/manage/competitions/${slug}/stop`, { stopReason });

  return response.data.data;
};

export const resumeCompetition = async (
  slug: string,
  params: IResumeCompetitionParams
): Promise<ManageCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetition>> =
    await apiClient.post(`/manage/competitions/${slug}/resume`, params);

  return response.data.data;
};

export const duplicateCompetition = async (
  slug: string,
  params: IDuplicateCompetitionParams
): Promise<string> => {
  const response: AxiosResponse<IUpsertSlugResourceResponse> =
    await apiClient.post(`/manage/competitions/${slug}/duplicate`, params);

  return response.data.data.slug;
};

export const completeCompetition = async (
  slug: string,
  completionMessage: string
): Promise<ManageCompetition> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetition>> =
    await apiClient.post(`/manage/competitions/${slug}/complete`, {
      completionMessage,
    });

  return response.data.data;
};

export const deleteCompetition = async (
  slug: string
): Promise<IDeleteResourceResponse> => {
  const response: AxiosResponse<IDeleteResourceResponse> =
    await apiClient.delete(`/manage/competitions/${slug}`);

  return response.data;
};

export const addCompetitionManagers = async (
  slug: string,
  memberIds: number[]
): Promise<INoDataResponse> => {
  const formData = convertObjectToFormData({
    memberIds,
  });

  const response: AxiosResponse<INoDataResponse> = await apiClient.post(
    `/manage/competitions/${slug}/managers`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
};

export const removeCompetitionManagers = async (
  slug: string,
  memberIds: number[]
): Promise<INoDataResponse> => {
  const response: AxiosResponse<INoDataResponse> = await apiClient.delete(
    `/manage/competitions/${slug}/managers`,
    {
      data: {
        memberIds,
      },
    }
  );

  return response.data;
};

export const getPortalCompetitionEnrollment = async (
  slug: string
): Promise<PortalCompetitionEnrollment> => {
  const response: AxiosResponse<
    IGetResourceResponse<PortalCompetitionEnrollment>
  > = await apiClient.get(`/competitions/${slug}/enrollments/me`);

  return response.data.data;
};

export const enrollInPortalCompetition = async (
  slug: string
): Promise<PortalCompetitionEnrollment> => {
  const response: AxiosResponse<
    IGetResourceResponse<PortalCompetitionEnrollment>
  > = await apiClient.post(`/competitions/${slug}/enrollments`);

  return response.data.data;
};

export const withdrawFromPortalCompetition = async (
  slug: string,
  reason?: string
): Promise<void> => {
  await apiClient.delete(`/competitions/${slug}/enrollments/me`, {
    data: reason !== undefined ? { reason } : undefined,
  });
};

export const getManageCompetitionEnrollmentWithdrawals = async (
  slug: string,
  page: number,
  pageSize: number,
  sort?: CompetitionEnrollmentWithdrawalsSort,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<CompetitionEnrollmentWithdrawal>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<CompetitionEnrollmentWithdrawal>
  > = await apiClient.get(
    `/manage/competitions/${slug}/enrollment-withdrawals`,
    {
      params: getPaginatedEndpointParams({
        page,
        perPage: pageSize,
        sort,
        search,
      }),
    }
  );

  return response.data;
};

export const storePortalScoreSubmissionImage = async (
  slug: string,
  image: File | Blob,
  filename: string
): Promise<string> => {
  const formData = new FormData();
  formData.append('image', image, filename);

  const response: AxiosResponse<IGetResourceResponse<{ id: string }>> =
    await apiClient.post(`/competitions/${slug}/submissions/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

  return response.data.data.id;
};

export const submitPortalScoreSubmission = async (
  slug: string,
  params: ISubmitScoreSubmissionParams
): Promise<PortalScoreSubmission> => {
  const response: AxiosResponse<IGetResourceResponse<PortalScoreSubmission>> =
    await apiClient.post(`/competitions/${slug}/submissions`, params);

  return response.data.data;
};

export const getPortalScoreSubmissions = async (
  slug: string
): Promise<PortalScoreSubmission[]> => {
  const response: AxiosResponse<IGetResourceResponse<PortalScoreSubmission[]>> =
    await apiClient.get(`/competitions/${slug}/submissions/me`);

  return response.data.data;
};

export const deletePortalScoreSubmission = async (
  slug: string,
  submissionId: number
): Promise<void> => {
  await apiClient.delete(`/competitions/${slug}/submissions/${submissionId}`);
};

export const getPortalCompetitionRanking = async (
  slug: string
): Promise<PortalCompetitionRanking> => {
  const response: AxiosResponse<PortalCompetitionRanking> = await apiClient.get(
    `/competitions/${slug}/ranking`
  );

  return response.data;
};

export const getPortalCompletedCompetitionRanking = async (
  slug: string
): Promise<PortalCompetitionRanking> => {
  const response: AxiosResponse<PortalCompetitionRanking> = await apiClient.get(
    `/competitions/results/${slug}/ranking`
  );

  return response.data;
};

export const getPortalCompetitionSessions = async (
  slug: string
): Promise<PortalCompetitionSession[]> => {
  const response: AxiosResponse<IGetResourceResponse<PortalCompetitionSession[]>> =
    await apiClient.get(`/competitions/${slug}/sessions`);

  return response.data.data;
};

export const getPortalCompletedCompetitionSessions = async (
  slug: string
): Promise<PortalCompetitionSession[]> => {
  const response: AxiosResponse<IGetResourceResponse<PortalCompetitionSession[]>> =
    await apiClient.get(`/competitions/results/${slug}/sessions`);

  return response.data.data;
};

export const getPortalCompetitionSessionRanking = async (
  slug: string,
  sessionId: number
): Promise<PortalCompetitionRanking> => {
  const response: AxiosResponse<PortalCompetitionRanking> = await apiClient.get(
    `/competitions/${slug}/sessions/${sessionId}/ranking`
  );

  return response.data;
};

export const getPortalCompletedCompetitionSessionRanking = async (
  slug: string,
  sessionId: number
): Promise<PortalCompetitionRanking> => {
  const response: AxiosResponse<PortalCompetitionRanking> = await apiClient.get(
    `/competitions/results/${slug}/sessions/${sessionId}/ranking`
  );

  return response.data;
};

export const getManageCompetitionEnrollments = async (
  slug: string,
  page: number,
  pageSize: number,
  sort?: CompetitionEnrollmentsSort,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<CompetitionEnrollment>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<CompetitionEnrollment>
  > = await apiClient.get(`/manage/competitions/${slug}/enrollments`, {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      sort,
      search,
    }),
  });

  return response.data;
};

export const getManageCompetitionSubmissions = async (
  slug: string,
  page: number,
  pageSize: number,
  sort?: CompetitionSubmissionsSort,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<ManageScoreSubmission>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<ManageScoreSubmission>
  > = await apiClient.get(`/manage/competitions/${slug}/submissions`, {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      sort,
      search,
    }),
  });

  return response.data;
};

export const getManageCompetitionSubmission = async (
  slug: string,
  submissionId: number
): Promise<ManageScoreSubmission> => {
  const response: AxiosResponse<IGetResourceResponse<ManageScoreSubmission>> =
    await apiClient.get(
      `/manage/competitions/${slug}/submissions/${submissionId}`
    );

  return response.data.data;
};

export const approveManageScoreSubmission = async (
  slug: string,
  submissionId: number
): Promise<ManageScoreSubmission> => {
  const response: AxiosResponse<IGetResourceResponse<ManageScoreSubmission>> =
    await apiClient.post(
      `/manage/competitions/${slug}/submissions/${submissionId}/approve`
    );

  return response.data.data;
};

export const getManageCompetitionSessions = async (
  slug: string,
  page: number,
  pageSize: number,
  sort?: CompetitionSessionsSort,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<ManageCompetitionSession>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<ManageCompetitionSession>
  > = await apiClient.get(`/manage/competitions/${slug}/sessions`, {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      sort,
      search,
    }),
  });

  return response.data;
};

export const getManageCompetitionSession = async (
  slug: string,
  sessionId: number
): Promise<ManageCompetitionSession> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSession>> =
    await apiClient.get(`/manage/competitions/${slug}/sessions/${sessionId}`);

  return response.data.data;
};

export const createManageCompetitionSession = async (
  slug: string,
  params: IUpsertCompetitionSessionParams
): Promise<ManageCompetitionSession> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSession>> =
    await apiClient.post(`/manage/competitions/${slug}/sessions`, params);

  return response.data.data;
};

export const updateManageCompetitionSession = async ({
  slug,
  sessionId,
  ...params
}: IUpsertCompetitionSessionParams & {
  slug: string;
  sessionId: number;
}): Promise<ManageCompetitionSession> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSession>> =
    await apiClient.put(
      `/manage/competitions/${slug}/sessions/${sessionId}`,
      params
    );

  return response.data.data;
};

export const deleteManageCompetitionSession = async ({
  slug,
  sessionId,
}: {
  slug: string;
  sessionId: number;
}): Promise<INoDataResponse> => {
  const response: AxiosResponse<INoDataResponse> = await apiClient.delete(
    `/manage/competitions/${slug}/sessions/${sessionId}`
  );

  return response.data;
};

export const startManageCompetitionSession = async ({
  slug,
  sessionId,
}: {
  slug: string;
  sessionId: number;
}): Promise<ManageCompetitionSession> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSession>> =
    await apiClient.post(
      `/manage/competitions/${slug}/sessions/${sessionId}/start`
    );

  return response.data.data;
};

export const completeManageCompetitionSession = async ({
  slug,
  sessionId,
}: {
  slug: string;
  sessionId: number;
}): Promise<ManageCompetitionSession> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSession>> =
    await apiClient.post(
      `/manage/competitions/${slug}/sessions/${sessionId}/complete`
    );

  return response.data.data;
};

export const cancelManageCompetitionSession = async ({
  slug,
  sessionId,
  cancellationReason,
}: {
  slug: string;
  sessionId: number;
  cancellationReason: string;
}): Promise<ManageCompetitionSession> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSession>> =
    await apiClient.post(
      `/manage/competitions/${slug}/sessions/${sessionId}/cancel`,
      { cancellationReason }
    );

  return response.data.data;
};

export const getManageCompetitionSessionRanking = async (
  slug: string,
  sessionId: number
): Promise<PortalCompetitionRanking> => {
  const response: AxiosResponse<PortalCompetitionRanking> = await apiClient.get(
    `/manage/competitions/${slug}/sessions/${sessionId}/ranking`
  );

  return response.data;
};

const sessionSeriesBasePath = (
  slug: string,
  sessionId: number
): string => `/manage/competitions/${slug}/sessions/${sessionId}/series`;

export const getManageCompetitionSessionSeriesList = async (
  slug: string,
  sessionId: number
): Promise<ManageCompetitionSeries[]> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSeries[]>> =
    await apiClient.get(sessionSeriesBasePath(slug, sessionId));

  return response.data.data;
};

export const getManageCompetitionSessionSeries = async (
  slug: string,
  sessionId: number,
  seriesId: number
): Promise<ManageCompetitionSeries> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSeries>> =
    await apiClient.get(`${sessionSeriesBasePath(slug, sessionId)}/${seriesId}`);

  return response.data.data;
};

export const createManageCompetitionSessionSeries = async ({
  slug,
  sessionId,
}: {
  slug: string;
  sessionId: number;
}): Promise<ManageCompetitionSeries> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSeries>> =
    await apiClient.post(sessionSeriesBasePath(slug, sessionId));

  return response.data.data;
};

export const startManageCompetitionSessionSeries = async ({
  slug,
  sessionId,
  seriesId,
}: {
  slug: string;
  sessionId: number;
  seriesId: number;
}): Promise<ManageCompetitionSeries> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSeries>> =
    await apiClient.post(
      `${sessionSeriesBasePath(slug, sessionId)}/${seriesId}/start`
    );

  return response.data.data;
};

export const completeManageCompetitionSessionSeries = async ({
  slug,
  sessionId,
  seriesId,
}: {
  slug: string;
  sessionId: number;
  seriesId: number;
}): Promise<ManageCompetitionSeries> => {
  const response: AxiosResponse<IGetResourceResponse<ManageCompetitionSeries>> =
    await apiClient.post(
      `${sessionSeriesBasePath(slug, sessionId)}/${seriesId}/complete`
    );

  return response.data.data;
};

export const addManageCompetitionSessionSeriesParticipant = async ({
  slug,
  sessionId,
  seriesId,
  memberId,
  weaponGroup,
}: {
  slug: string;
  sessionId: number;
  seriesId: number;
  memberId: number;
  weaponGroup?: string;
}): Promise<ManageSeriesParticipant> => {
  const response: AxiosResponse<IGetResourceResponse<ManageSeriesParticipant>> =
    await apiClient.post(
      `${sessionSeriesBasePath(slug, sessionId)}/${seriesId}/participants`,
      { memberId, ...(weaponGroup ? { weaponGroup } : {}) }
    );

  return response.data.data;
};

export const updateManageCompetitionSessionSeriesParticipantWeaponGroup = async ({
  slug,
  sessionId,
  seriesId,
  participantId,
  weaponGroup,
}: {
  slug: string;
  sessionId: number;
  seriesId: number;
  participantId: number;
  weaponGroup: string;
}): Promise<ManageSeriesParticipant> => {
  const response: AxiosResponse<IGetResourceResponse<ManageSeriesParticipant>> =
    await apiClient.patch(
      `${sessionSeriesBasePath(slug, sessionId)}/${seriesId}/participants/${participantId}/weapon-group`,
      { weaponGroup }
    );

  return response.data.data;
};

export const removeManageCompetitionSessionSeriesParticipant = async ({
  slug,
  sessionId,
  seriesId,
  participantId,
}: {
  slug: string;
  sessionId: number;
  seriesId: number;
  participantId: number;
}): Promise<INoDataResponse> => {
  const response: AxiosResponse<INoDataResponse> = await apiClient.delete(
    `${sessionSeriesBasePath(slug, sessionId)}/${seriesId}/participants/${participantId}`
  );

  return response.data;
};

export const upsertManageCompetitionSessionSeriesParticipantScores = async ({
  slug,
  sessionId,
  seriesId,
  participantId,
  scores,
}: {
  slug: string;
  sessionId: number;
  seriesId: number;
  participantId: number;
  scores: IUpsertSeriesParticipantScoresParams['scores'];
}): Promise<ManageSeriesParticipant> => {
  const response: AxiosResponse<IGetResourceResponse<ManageSeriesParticipant>> =
    await apiClient.put(
      `${sessionSeriesBasePath(slug, sessionId)}/${seriesId}/participants/${participantId}/scores`,
      { scores }
    );

  return response.data.data;
};

export const rejectManageScoreSubmission = async ({
  slug,
  submissionId,
  reviewNote,
}: {
  slug: string;
  submissionId: number;
  reviewNote?: string | null;
}): Promise<ManageScoreSubmission> => {
  const response: AxiosResponse<IGetResourceResponse<ManageScoreSubmission>> =
    await apiClient.post(
      `/manage/competitions/${slug}/submissions/${submissionId}/reject`,
      {
        reviewNote,
      }
    );

  return response.data.data;
};
